import { DetailModel, FieldModel, ProgramTask, TaskDataTraitStatus } from '@cibo/core'
import { useRemoveDetail } from '@cibo/landmanager'
import { CircularProgress, Typography } from '@mui/material'
import { Box } from '@mui/system'
import { GridRenderCellParams } from '@mui/x-data-grid-pro'
import { useCallback } from 'react'
import { TraitCellContainer } from './TraitCells/TraitCellContainer'
import { TRAIT_DISPLAY_MAP } from './traitDisplayMap'

const formatDetailValue = (
  detail: DetailModel | undefined,
  detailValueKey: TaskDataTraitStatus['detailValueKey']
): string => {
  if (!detail) return ''
  const raw = (detail as unknown as Record<string, unknown>)[detailValueKey]
  if (raw === null || raw === undefined) return ''
  if (typeof raw === 'boolean') return raw ? 'Yes' : 'No'
  if (typeof raw === 'number' || typeof raw === 'string') return String(raw)
  return JSON.stringify(raw)
}

interface FieldTraitTaskEditorTableCellProps extends GridRenderCellParams {
  row: { fieldModel: FieldModel; [key: string]: any }
  field: string // traitId
  value: DetailModel | undefined
  task: ProgramTask<{ traits: TaskDataTraitStatus[] }>
  onSaveSuccess?: () => void
  onDeleteSuccess?: () => void
  isLoading?: boolean
}

export const FieldTraitTaskEditorTableCell = ({
  row: { fieldModel },
  value,
  field: traitId,
  task,
  onSaveSuccess,
  onDeleteSuccess,
  isLoading,
}: FieldTraitTaskEditorTableCellProps) => {
  const Display = TRAIT_DISPLAY_MAP[traitId]
  const removeDetail = useRemoveDetail()
  const traitConfig = task.rule.params?.traits?.find(t => t.traitId === traitId)

  const handleDelete = useCallback(() => {
    if (!value || !fieldModel.resourceId) return
    removeDetail.mutateAsync({ resourceId: fieldModel.resourceId, traitId }).then(() => {
      onDeleteSuccess?.()
    })
  }, [value, fieldModel.resourceId, traitId, removeDetail, onDeleteSuccess])

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <TraitCellContainer
        value={value}
        task={task as any}
        selectedField={fieldModel}
        traitId={traitId}
        onSaveSuccess={onSaveSuccess}
        onDelete={handleDelete}
      >
        {Display ? (
          <Display value={value} />
        ) : (
          <Typography variant="body2">
            {formatDetailValue(value, traitConfig?.detailValueKey ?? 'result')}
          </Typography>
        )}
      </TraitCellContainer>
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'action.hover',
          }}
        >
          <CircularProgress size={16} />
        </Box>
      )}
    </Box>
  )
}
